<!-- eslint-disable vue/no-v-html -->
<template>
  <div class="load-more-articles">
    <slot v-if="!loaded" />
    <slot
      v-if="emptyList"
      name="no-results"
    />
    <ul
      v-if="loaded && !emptyList"
      :class="['article-grid', {'article-grid--landing': landingArticles}]"
    >
      <li
        v-for="(article, i) in loadList"
        :key="`key_${i}_${Date.now()}_${(Math.random()*1000000).toFixed(0)}`"
      >
        <a
          v-tag:useractionevent="{
            action: 'select',
            category: article.pageType,
            cta_name: 'select',
            ecommerce: undefined,
            event: 'uaevent',
            event_detail: `${i + 1}::${article.titlePlainText}`,
            event_name: 'article_click',
            label: `${i + 1}::${article.titlePlainText}`,
          }"
          :class="['article-card', {'article-card--landing': landingArticles}]"
          :href="article.ctaLink"
          :title="article.image.alt"
        >
          <div class="image image--cover">
            <img
              v-srcset="article.image.srcset"
              :alt="article.image.alt"
              class="image__item"
              src="data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw=="
            >
          </div>
          <div :class="{'article-card--landing__text': landingArticles}">
            <p
              v-if="!landingArticles"
              class="article-card__surtitle"
              v-text="article.surtitle"
            />
            <div class="article-card__title">
              <span v-html="article.title" />
            </div>
            <p
              v-lineclamp="3"
              class="article-card__description"
              v-html="article.description"
            />
            <p
              v-if="landingArticles && article.ctaLabel"
              class="article-card--landing__cta-label"
            >
              {{ article.ctaLabel }}
              <svg class="icon">
                <use xlink:href="#chevron-right" />
              </svg>
            </p>
          </div>
        </a>
      </li>
    </ul>
    <div
      :aria-live="hasMoreArticlesToLoad ? 'polite' : 'off'"
      :class="['load-more-articles__loading', {'load-more-articles__loading--show' : loading}]"
    >
      <div v-if="loading">
        <span class="is-sr-only">{{ loadingStateLabel }}</span>
        <slot name="loading" />
      </div>
    </div>
    <div class="load-more-articles__cta">
      <button
        v-if="hasMoreArticlesToLoad && !emptyList"
        class="btn btn--ghost"
        @click="loadMoreArticles"
      >
        {{ ctaLabel }}
      </button>
    </div>
  </div>
</template>

<script>
  import { ellipsis } from '@Foundation/directives/ellipsis/ellipsis';
  import { srcset } from '@Foundation/directives/srcset/srcset';
  import { lineclamp } from '@Foundation/directives/lineclamp/lineclamp';
  import { tag } from '@Foundation/analyticsHandler/directives/tag';
  import { mapGetters } from 'vuex';

  export default {
    name: 'LoadMoreArticlesSearch',
    directives: {
      ellipsis,
      srcset,
      lineclamp,
      tag
    },
    props: {
      apiUrl: {
        type: String,
        required: true
      },
      loadMoreCtaLabel: {
        type: String,
        required: false,
        default: 'Load More'
      },
      loadingStateLabel: {
        type: String,
        required: false,
        default: 'Loading more results'
      },
      hasMore: {
        type: Boolean,
        required: false,
        default: false
      },
      initialArticles: {
        type: Array,
        required: true,
        default: undefined
      },
      landingArticles: {
        type: Boolean,
        required: false,
        default: false
      },
      loadParameters: {
        type: Object,
        required: true
      },
      loadMoreCounter: {
        type: Object,
        required: false,
        default: null
      }
    },
    data() {
      return {
        url: null,
        loadList: [],
        hasMoreArticlesToLoad: true,
        loading: false,
        loaded: false,
        parameters: null,
        itemsLeft: null
      };
    },
    computed: {
      ...mapGetters('searchFiltersStore', [
        'selectedArticlesFiltersIds',
        'selectedArticlesSorting'
      ]),
      ctaLabel() {
        if (this.loadMoreCounter) {
          return this.itemsLeft <= this.loadMoreCounter.moreItemsNumber ? `${this.loadMoreCounter.loadMoreCtaLabelPart1} ${this.itemsLeft} ${this.loadMoreCounter.loadMoreCtaLabelPart2}` : `${this.loadMoreCounter.loadMoreCtaLabelPart1} ${this.loadMoreCounter.moreItemsNumber} ${this.loadMoreCounter.loadMoreCtaLabelPart2}`;
        }
        return this.loadMoreCtaLabel;
      },
      emptyList() {
        return this.loadList.length === 0;
      }
    },
    mounted() {
      this.loadList = this.initialArticles || [];
      this.url = this.apiUrl;
      this.hasMoreArticlesToLoad = this.hasMore;
      this.loaded = true;
      this.parameters = this.loadParameters;
      if (this.loadMoreCounter) {
        this.itemsLeft = this.loadMoreCounter.totalItemsNumber - this.loadMoreCounter.initialItemsNumber;
      }
    },
    methods: {
      loadMoreArticles() {
        this.loading = true;
        this.parameters.selectedFilters = this.selectedArticlesFiltersIds;
        this.parameters.selectedSorting = this.selectedArticlesSorting;
        const parseJSON = (response) => response.json();
        fetch(this.url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json;charset=utf-8' },
          credentials: 'same-origin',
          body: JSON.stringify(this.parameters)
        })
          .then(parseJSON)
          .then(data => {
            this.handleResponse(data);
          })
          .catch(error => {
            this.loading = false;
            console.error('Could not load articles', error); // eslint-disable-line no-console
          });
      },
      resetComponent(loadList, parameters, itemsLeft, hasMore) {
        this.loadList = loadList;
        this.parameters = parameters;
        this.itemsLeft = itemsLeft;
        this.hasMoreArticlesToLoad = hasMore;
      },
      handleResponse(data) {
        if (data.loadParameters) {
          this.parameters = data.loadParameters;
        }
        if (data.results) {
          this.loadList = [].concat(this.loadList, data.results);
          this.hasMoreArticlesToLoad = this.itemsLeft <= 0 ? false : data.hasMore;
        }
        if (this.loadMoreCounter) {
          this.itemsLeft -= this.loadMoreCounter.moreItemsNumber;
        }
        this.loading = false;
      }
    }
  };
</script>

<style src="../LoadMoreArticles/LoadMoreArticles.scss" lang="scss">
